/**
 * Friendly label for the user's current browser timezone, e.g.
 *   "Eastern Daylight Time (EDT, UTC-4)"
 *   "Pacific Standard Time (PST, UTC-8)"
 *   "Central European Time (CET, UTC+1)"
 *
 * Surfaced next to <input type="datetime-local"> fields so admins editing
 * a game know which clock the typed value will be interpreted in. The form
 * always converts the local-time string to UTC ISO before saving.
 */
export function describeLocalTimezone(date = new Date()) {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Try to extract a friendly long name + short abbreviation from Intl
    const longParts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'long',
    }).formatToParts(date);
    const shortParts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(date);
    const long = longParts.find(p => p.type === 'timeZoneName')?.value || tz;
    const short = shortParts.find(p => p.type === 'timeZoneName')?.value || '';

    // Compute UTC offset hours (positive east, negative west)
    const offsetMinutes = -date.getTimezoneOffset(); // getTimezoneOffset is inverted
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMin = Math.abs(offsetMinutes);
    const hh = Math.floor(absMin / 60);
    const mm = absMin % 60;
    const offsetStr = `UTC${sign}${hh}${mm ? `:${String(mm).padStart(2, '0')}` : ''}`;

    if (short && short !== long) {
      return `${long} (${short}, ${offsetStr})`;
    }
    return `${long} (${offsetStr})`;
  } catch {
    // Older browsers / locked-down envs — fall back to bare offset
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMin = Math.abs(offsetMinutes);
    return `UTC${sign}${Math.floor(absMin / 60)}${absMin % 60 ? `:${String(absMin % 60).padStart(2, '0')}` : ''}`;
  }
}
